import { Button, H5, H6, InputDate } from "@sellar-io/components";
import { useEffect } from "react";
import { InputLabel, InputContainer } from "@sellar-io/components";
import {
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../../../components/modal/Dialog";
import {
  RadioGroup,
  RadioGroupOption,
  RadioGroupContainer,
} from "../../../../../components/radioSelector/RadioGroup";
import dayjs from "dayjs";
import { Icon } from "../../../../../components";
import { useForm, useWatch } from "react-hook-form";
import { useRemoteFeatureFlags } from "../../../../../hooks/useRemoteFeatureFlags";
import { PreorderMarketingCard } from "../../components/PreorderMarketingCard";

export type ItemReleaseType =
  | {
      releaseType: "generalRelease";
      availableFrom: null;
    }
  | {
      releaseType: "futureRelease";
      availableFrom: Date;
    };

const NewTag = () => {
  return (
    <div className="shrink-0 rounded-xl border border-indigo-500 bg-indigo-50 px-2 py-1 text-black text-zinc-800">
      New
    </div>
  );
};

export default function ItemReleaseDialogContent({
  itemRelease,
  onDismiss,
  onSubmitItemReleaseType,
  onBack,
}: {
  itemRelease: ItemReleaseType;
  onDismiss: () => void;
  onSubmitItemReleaseType: (data: ItemReleaseType) => void;
  onBack: () => void;
}) {
  const remoteFeatureFlag = useRemoteFeatureFlags();
  const { register, handleSubmit, reset, control, setValue } = useForm<
    Omit<ItemReleaseType, "availableFrom"> & { availableFrom: string }
  >({
    shouldUseNativeValidation: true,
  });

  useEffect(() => {
    reset({
      ...itemRelease,
      availableFrom: itemRelease.availableFrom
        ? dayjs(itemRelease.availableFrom).format("YYYY-MM-DD")
        : undefined,
    });
  }, [reset, itemRelease]);

  const releaseType = useWatch({
    control,
    name: "releaseType",
  });

  return (
    <form
      onSubmit={handleSubmit((data) => {
        if (data.releaseType === "futureRelease") {
          return onSubmitItemReleaseType({
            releaseType: "futureRelease",
            availableFrom: dayjs(data.availableFrom).toDate(),
          });
        }

        onSubmitItemReleaseType({
          ...data,
          releaseType: "generalRelease",
          availableFrom: null,
        });
      })}
    >
      <input type="hidden" {...register("releaseType")} />
      <DialogNavigation
        title="Item release"
        description="Choose how you want customers to see this item"
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
      />

      <DialogContent>
        <div className="my-6">
          <PreorderMarketingCard />
        </div>
        <div className="mb-2 flex">
          <H5>Item release</H5>
        </div>

        <RadioGroup
          value={releaseType}
          onChange={(value) =>
            setValue("releaseType", value as "generalRelease" | "futureRelease")
          }
        >
          <RadioGroupContainer>
            <RadioGroupOption value={"generalRelease"} className={"p-4"}>
              <div className="flex flex-col">
                <H5>General release</H5>
                <p>Deliveries for this product can start today.</p>
              </div>
            </RadioGroupOption>

            {remoteFeatureFlag.isActive("preOrders") && (
              <RadioGroupOption value={"futureRelease"} className={"p-4"}>
                <div className="flex basis-full items-center justify-between">
                  <div className="flex flex-col justify-start">
                    <H5>Future release</H5>
                    <p>
                      Deliveries for this product can start on a future date.
                    </p>
                  </div>

                  <NewTag />
                </div>
              </RadioGroupOption>
            )}
          </RadioGroupContainer>
        </RadioGroup>

        {releaseType === "futureRelease" && (
          <div className="mt-4 flex flex-col gap-1">
            <div>
              <InputLabel>Available from</InputLabel>
              <H6>
                Select the date when deliveries for this product will begin.
              </H6>

              <InputContainer>
                <InputDate
                  min={dayjs()
                    .add(1, "day")
                    .startOf("day")
                    .format("YYYY-MM-DD")}
                  {...register("availableFrom", {
                    valueAsDate: true,
                  })}
                  required={true}
                />
              </InputContainer>
            </div>

            <div className={"flex items-center gap-1 text-xs"}>
              <Icon
                icon={"INFORMATION_OUTLINE"}
                size={0.6}
                className="shrink-0"
              />
              <div className="text-sm text-zinc-500">
                Customers can pre-order this product. Their estimate delivery
                date will be calculated from the date you select, based on your
                delivery schedules.
              </div>
            </div>
          </div>
        )}
      </DialogContent>

      <DialogCTA>
        <div className="flex w-full items-center justify-between">
          <Button color="tertiary" onClick={onBack}>
            <div className="flex">
              <Icon icon="CHEVRON_LEFT" />
              <div>Back</div>
            </div>
          </Button>
          <Button type="submit">Continue</Button>
        </div>
      </DialogCTA>
    </form>
  );
}
